import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { isAdmin } from '../../services/authService';
import { getAllUsers, grantAdminRole, revokeAdminRole, deactivateUser, User, deauthorizeStravaToken } from '../../services/userManagementService';
import './styles.css';

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [actionUser, setActionUser] = useState<User | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [dialogType, setDialogType] = useState<'grant' | 'revoke' | 'deactivate' | 'deauthorize'>('grant');
  const [isActionLoading, setIsActionLoading] = useState<boolean>(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [searchTerm, users]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAllUsers();
      setUsers(data);
      setFilteredUsers(data);
    } catch (error: any) {
      console.error('Lỗi khi lấy danh sách người dùng:', error);
      setError(error.message || 'Không thể lấy danh sách người dùng');
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    if (!searchTerm.trim()) {
      setFilteredUsers(users);
      return;
    }
    
    const lowercasedSearch = searchTerm.toLowerCase();
    const filtered = users.filter(user => {
      const fullName = `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase();
      const username = user.username.toLowerCase();
      const email = user.email.toLowerCase();
      
      return fullName.includes(lowercasedSearch) || 
             username.includes(lowercasedSearch) ||
             email.includes(lowercasedSearch);
    });
    
    setFilteredUsers(filtered);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleOpenDialog = (user: User, type: 'grant' | 'revoke' | 'deactivate' | 'deauthorize') => {
    setActionUser(user);
    setDialogType(type);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setActionUser(null);
  };

  const handleConfirmAction = async () => {
    if (!actionUser) return;

    try {
      setIsActionLoading(true);
      let result;

      if (dialogType === 'grant') {
        result = await grantAdminRole(actionUser.id);
      } else if (dialogType === 'revoke') {
        result = await revokeAdminRole(actionUser.id);
      } else if (dialogType === 'deactivate') {
        result = await deactivateUser(actionUser.id);
      } else if (dialogType === 'deauthorize') {
        result = await deauthorizeStravaToken(actionUser.id);
      }

      if (result && result.success) {
        alert(result.message);
        // Cập nhật lại danh sách người dùng sau khi thao tác thành công
        fetchUsers();
      } else if (result) {
        alert(result.message);
      }
    } catch (error: any) {
      alert(error.message || 'Đã xảy ra lỗi khi thực hiện thao tác');
    } finally {
      setIsActionLoading(false);
      handleCloseDialog();
    }
  };

  const isUserAdmin = (user: User): boolean => {
    if (!user.role) return false;
    return user.role.includes('ADMIN') || user.role === 'ROLE_ADMIN';
  };

  // Kiểm tra xem người dùng có phải là tài khoản admin mặc định không
  const isDefaultAdminAccount = (user: User): boolean => {
    return user.username === 'admin';
  };

  // Kiểm tra quyền admin
  if (!isAdmin()) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="container-fluid p-4">
      <div className="page-header">
        <h1 className="page-title">Quản lý người dùng</h1>
        <p className="page-subtitle">Quản lý người dùng và phân quyền trong hệ thống</p>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="search-container">
        <input
          type="text"
          placeholder="Tìm kiếm theo tên, tên đăng nhập hoặc email..."
          value={searchTerm}
          onChange={handleSearchChange}
          className="search-input"
        />
      </div>

      {loading ? (
        <div className="loading-indicator">Đang tải dữ liệu...</div>
      ) : (
        <div className="user-table-container">
          <table className="user-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Tên đăng nhập</th>
                <th>Email</th>
                <th>Họ và tên</th>
                <th>Quyền</th>
                <th>Trạng thái</th>
                <th>Kết nối Strava</th>
                <th>Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length > 0 ? (
                filteredUsers.map((user) => (
                  <tr 
                    key={user.id} 
                    className={`${!user.active ? 'inactive-user' : ''} ${isDefaultAdminAccount(user) ? 'admin-account-row' : ''}`}
                  >
                    <td>{user.id}</td>
                    <td>{user.username}</td>
                    <td>{user.email}</td>
                    <td>{`${user.firstName || ''} ${user.lastName || ''}`}</td>
                    <td>
                      <span className={`badge ${isUserAdmin(user) ? 'badge-admin' : 'badge-user'}`}>
                        {isUserAdmin(user) ? 'Admin' : 'User'}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${user.active ? 'badge-active' : 'badge-inactive'}`}>
                        {user.active ? 'Hoạt động' : 'Vô hiệu hóa'}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${user.hasStravaToken ? 'badge-connected' : 'badge-disconnected'}`}>
                        {user.hasStravaToken ? 'Đã kết nối' : 'Chưa kết nối'}
                      </span>
                    </td>
                    <td className="action-buttons">
                      {isDefaultAdminAccount(user) ? (
                        <span className="admin-protection-note">Tài khoản admin hệ thống</span>
                      ) : (
                        <>
                          {isUserAdmin(user) ? (
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => handleOpenDialog(user, 'revoke')}
                            >
                              Thu hồi Admin
                            </button>
                          ) : (
                            <button
                              className="btn btn-success btn-sm"
                              onClick={() => handleOpenDialog(user, 'grant')}
                            >
                              Cấp quyền Admin
                            </button>
                          )}
                          
                          {user.active && (
                            <button
                              className="btn btn-warning btn-sm"
                              onClick={() => handleOpenDialog(user, 'deactivate')}
                            >
                              Vô hiệu hóa
                            </button>
                          )}
                          
                          {user.hasStravaToken && (
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => handleOpenDialog(user, 'deauthorize')}
                            >
                              Hủy kết nối Strava
                            </button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="text-center">Không tìm thấy người dùng phù hợp</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Dialog xác nhận thao tác */}
      {isDialogOpen && (
        <div className="modal-backdrop">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {dialogType === 'grant' 
                    ? 'Cấp quyền Admin' 
                    : dialogType === 'revoke' 
                      ? 'Thu hồi quyền Admin'
                      : dialogType === 'deactivate'
                        ? 'Vô hiệu hóa tài khoản'
                        : 'Hủy kết nối Strava'
                  }
                </h5>
                <button type="button" className="close-button" onClick={handleCloseDialog}>×</button>
              </div>
              <div className="modal-body">
                {dialogType === 'grant' 
                  ? (
                    <>
                      <p>Bạn có chắc muốn cấp quyền Admin cho người dùng sau đây không?</p>
                      <div className="user-info">
                        <p><strong>Tên người dùng:</strong> {actionUser?.username}</p>
                        <p><strong>Họ tên:</strong> {`${actionUser?.firstName || ''} ${actionUser?.lastName || ''}`}</p>
                        <p><strong>Email:</strong> {actionUser?.email}</p>
                      </div>
                      <p className="warning-text">Lưu ý: Người dùng này sẽ có toàn quyền quản lý hệ thống sau khi được cấp quyền.</p>
                    </>
                  )
                  : dialogType === 'revoke'
                    ? (
                      <>
                        <p>Bạn có chắc muốn thu hồi quyền Admin của người dùng sau đây không?</p>
                        <div className="user-info">
                          <p><strong>Tên người dùng:</strong> {actionUser?.username}</p>
                          <p><strong>Họ tên:</strong> {`${actionUser?.firstName || ''} ${actionUser?.lastName || ''}`}</p>
                          <p><strong>Email:</strong> {actionUser?.email}</p>
                        </div>
                        <p className="warning-text">Lưu ý: Người dùng này sẽ không còn quyền quản lý hệ thống sau khi bị thu hồi quyền.</p>
                      </>
                    )
                    : dialogType === 'deactivate'
                      ? (
                        <>
                          <p>Bạn có chắc muốn vô hiệu hóa tài khoản của người dùng sau đây không?</p>
                          <div className="user-info">
                            <p><strong>Tên người dùng:</strong> {actionUser?.username}</p>
                            <p><strong>Họ tên:</strong> {`${actionUser?.firstName || ''} ${actionUser?.lastName || ''}`}</p>
                            <p><strong>Email:</strong> {actionUser?.email}</p>
                          </div>
                          <p className="warning-text">Lưu ý: Người dùng này sẽ không thể đăng nhập sau khi bị vô hiệu hóa tài khoản.</p>
                        </>
                      )
                      : (
                        <>
                          <p>Bạn có chắc muốn hủy kết nối Strava của người dùng sau đây không?</p>
                          <div className="user-info">
                            <p><strong>Tên người dùng:</strong> {actionUser?.username}</p>
                            <p><strong>Họ tên:</strong> {`${actionUser?.firstName || ''} ${actionUser?.lastName || ''}`}</p>
                            <p><strong>Email:</strong> {actionUser?.email}</p>
                          </div>
                          <p className="warning-text">Lưu ý: Người dùng này sẽ không còn kết nối với Strava và cần phải kết nối lại để sử dụng các tính năng liên quan.</p>
                        </>
                      )
                }
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={handleCloseDialog}
                >
                  Hủy
                </button>
                <button 
                  type="button" 
                  className="btn btn-primary"
                  onClick={handleConfirmAction}
                  disabled={isActionLoading}
                >
                  {isActionLoading 
                    ? 'Đang xử lý...' 
                    : dialogType === 'grant' 
                      ? 'Xác nhận cấp quyền' 
                      : dialogType === 'revoke' 
                        ? 'Xác nhận thu hồi'
                        : dialogType === 'deactivate'
                          ? 'Xác nhận vô hiệu hóa'
                          : 'Xác nhận hủy kết nối'
                  }
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement; 